<template>
  <div id="alignment" :class="alignment">
    <view-linkable :content-element="contentElement" :entry="entry" :disabled="isInDetailViewEditMode">
      <blank-fallback :isBlank="isBlank(value)">
        <list-format-display v-if="shouldLinkify"
                             :class="isInDetailViewEditMode ? 'disabled-link' : 'btn-link'"
                             :href="value"
                             :is-list="displayAsList"
                             :value="value"
                             tag="a"
        />
        <list-format-display v-else :is-list="displayAsList" :value="value" tag="span" />

        <button v-if="propertyHasSecurityMaskToggle" @click="toggleSecurityMask" class="btn-icon" :class="securityMaskActive ? 'light-grey' : 'grey'">
          <span class="sr-only">{{securityMaskActive ? 'Show' : 'Hide'}} masked data</span>
          <icon :class="securityMaskActive ? 'hide' : 'show'"></icon>
        </button>
      </blank-fallback>
    </view-linkable>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import listViewTableMixin from "../mixins/listViewTableMixin";
  import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
  import {ObjectModelPropertyRootType} from "~/enums/objectModelPropertyType";
  import {ObjectModelBuiltinPropertyType} from "~/enums/objectModelBuiltinPropertyType";
  import ObjectModelPropertyValue from "~/models/objectModelProperties/objectModelPropertyValue";
  import selectedMixin from "../mixins/selectedMixin";
  import SecurityMaskableViewer from "~/models/securityMaskableViewer";
  import {InputFormatType} from '../../form_builder/models/inputFormatType'
  import {NumberHelper} from "~/helpers/numberHelper"
  import {AlignmentType} from "~/models/listViews/alignmentType"
  import {StringHelper} from "~/helpers/stringHelper"
  import {ListFormatType} from '~/enums/listFormatType'
  import ListFormatDisplay from '~/components/content/list-format-display.vue'

  export default Vue.extend({
    components: {
      ListFormatDisplay,
    },
    mixins: [
      listViewTableMixin,
      selectedMixin,
    ],
    data: function () {
      return {
        securityMaskActive: true,
        modelProperty: null
      };
    },
    created() {
      // because this finds the ObjectModelProperty by iterating through all of them,
      // if this.modelProperty was a computed var, it'll actually make this component keep a watcher/observer on
      // all properties, which gets expensive when there are many of this component on a list view page.
      //
      // this is just one hacky way to make the search result non-reactive.
      this.modelProperty = this.contentElement.property
    },
    computed: {
      propertyIsDateTime(): boolean {
        return this.contentElement.propertyIsDateTime
      },
      propertySecurityMaskEnabled(): boolean {
        return this.modelProperty && this.modelProperty.is_security_maskable_enabled;
      },
      propertyHasSecurityMaskToggle(): boolean {
        return this.propertySecurityMaskEnabled &&
          this.modelProperty.securityMaskingFullViewersArray.some((x: SecurityMaskableViewer<ObjectModelProperty>) => {
            return x.matches(this.selectedUser, this.entry);
          });
      },
      entryValue(): string {
        if (this.propertyIsDateTime) {
          const value = this.entry.valueFor(this.modelProperty.client_id);
          return ObjectModelPropertyValue.parseDate(value, this.contentElement, this.modelProperty)
        }

        if (this.displayAsList) {
          return this.entry.valueFor(this.modelProperty.client_id).map(this.modelProperty.getLabel.bind(this.modelProperty))
        }

        if (this.propertySecurityMaskEnabled) {
          const value = this.entry.displayValueFor(this.modelProperty.client_id);
          if (this.propertyHasSecurityMaskToggle) {
            return this.securityMaskActive ? value?.masked : value?.unmasked;
          } else {
            return value?.masked;
          }
        }

        return this.entry.displayValueFor(this.modelProperty.client_id);
      },
      value() {
        if (this.modelProperty) {
          const isNumber = (v) => typeof v === 'number' ? NumberHelper.isNumber(v) : StringHelper.isNumber(v)
          if (this.contentElement.format_type === InputFormatType.Currency && isNumber(this.entryValue)) {
            return NumberHelper.numberToCurrency("en-us", "USD", this.entryValue)
          }
          if (this.contentElement.format_type === InputFormatType.Percentage && isNumber(this.entryValue)) {
            const displayDecimalPlaces = Math.max(this.modelProperty.decimal_places - 2, 0);
            return (parseFloat(this.entryValue) * 100).toFixed(displayDecimalPlaces) + '%'
          }
          return this.entryValue;
        }

        if (this.contentElement.builtinPropertyType) {
          let val = this.entry.valueForBuiltin(this.contentElement.builtinPropertyType);
          if (this.contentElement.builtinPropertyType === ObjectModelBuiltinPropertyType.CreatedAt || this.contentElement.builtinPropertyType === ObjectModelBuiltinPropertyType.UpdatedAt){
            return ObjectModelPropertyValue.parseDate(val, this.contentElement, this.modelProperty)
          } else  {
            return val
          }
        }

        if (this.entry.view_data?.content_element_data?.[this.contentElement.client_id]) {
          return this.entry.view_data.content_element_data[this.contentElement.client_id];
        }

        return null;
      },
      shouldLinkify() {
        return !this.isInDetailViewEditMode && this.modelProperty?.isUrl && !this.contentElement.hasLinkedView;
      },
      alignment() {
        if (this.contentElement.alignment_type === AlignmentType.right) {
          return 'align-self-end'
        } else if (this.contentElement.alignment_type === AlignmentType.center) {
          return 'align-self-center'
        } else {
          return ''
        }
      },
      displayAsList(): boolean {
        return this.contentElement.list_format_type === ListFormatType.NewLine &&
          this.modelProperty &&
          this.modelProperty.isPickList &&
          this.modelProperty.isDataCollection
      },
    },

    methods: {
      toggleSecurityMask(): void {
        this.securityMaskActive = !this.securityMaskActive;
      },
      propertyIsCurrency(): boolean {
        if (this.modelProperty) {
          return (this.modelProperty.property_type === ObjectModelPropertyRootType.Number || this.modelProperty.mapped_property_type === ObjectModelPropertyRootType.Number) && this.modelProperty.format_type === InputFormatType.Currency
        }
      },
      propertyIsPercentage(): boolean {
        if (this.modelProperty) {
          return this.modelProperty.format_type === InputFormatType.Percentage
        }
      },
    },
  });
</script>